// src/DraftFromNotes.js
// Paste call notes, get a proposed agreement, review it line by line, apply.
//
// The review step is the point of this component. The server only ever returns
// a proposal; nothing reaches the builder until the user unchecks what's wrong
// and clicks Apply. Every proposed item is individually declinable, because the
// failure this guards against isn't a hallucinated product (the server drops
// those) — it's a plausible-looking guess the rep would otherwise not notice.
import { useState } from "react";

const PLACEHOLDER = `Paste your call notes, an email thread, or just type what they're buying.

e.g. Acme Health Partners — CQMsolution, 6 eCQMs, wants us to host it.
Adding UDS reporting, they have 3 FQHCs. No Cypress testing API.
Sayge agreed $12,000 for the software instead of list.`;

// A checkbox row. `on` items apply; unchecked ones are dropped on Apply.
function Row({ checked, onToggle, title, detail, note }) {
  return (
    <div onClick={onToggle}
         style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "7px 9px", cursor: "pointer",
                  borderRadius: 6, background: checked ? "#f6f8ff" : "transparent",
                  opacity: checked ? 1 : 0.5, border: "1px solid", borderColor: checked ? "#e0e6ff" : "transparent" }}>
      <input type="checkbox" checked={checked} onChange={onToggle} onClick={e => e.stopPropagation()}
             style={{ marginTop: 2, cursor: "pointer", flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, color: "#1c2333" }}>
          {title}
          {detail && <span style={{ color: "#8a90a6", fontWeight: 400 }}> — {detail}</span>}
        </div>
        {note && <div style={{ fontSize: 11, color: "#8a90a6", marginTop: 2 }}>{note}</div>}
      </div>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#8a90a6", letterSpacing: ".04em", marginBottom: 5 }}>
        {title.toUpperCase()}
      </div>
      {children}
    </div>
  );
}

export default function DraftFromNotes({ products, onApply, onClose }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);
  // Everything the model proposed starts checked; unchecking declines that item.
  const [off, setOff] = useState(() => new Set());

  const nameOf = (id) => products.find(p => p.id === id)?.name || id;
  const isOn = (key) => !off.has(key);
  const toggle = (key) => setOff(prev => {
    const n = new Set(prev);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  const build = async () => {
    setBusy(true); setError(null);
    try {
      const res = await fetch("/api/assist/draft", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || "Could not build a draft."); return; }
      setDraft(data.draft);
      setOff(new Set());
    } catch {
      setError("Could not reach the server.");
    } finally { setBusy(false); }
  };

  // Strip the declined items, then hand the rest to the builder.
  const apply = () => {
    const keptProducts = draft.products.filter(id => isOn(`p:${id}`));
    const keep = (id) => keptProducts.includes(id);
    onApply({
      products: keptProducts,
      hosting: draft.hosting.filter(id => isOn(`h:${id}`) && keep(id)),
      uds: draft.uds.filter(id => isOn(`u:${id}`) && keep(id)),
      excludeAddons: draft.excludeAddons.filter(a => isOn(`x:${a.rowId}`)).map(a => a.rowId),
      priceOverrides: draft.priceOverrides.filter(o => isOn(`$:${o.rowId}`)),
      fields: draft.fields.filter(f => isOn(`f:${f.key}`)),
      agreementTitle: isOn("title") ? draft.agreementTitle : "",
    });
  };

  const nothingLeft = draft && draft.products.every(id => off.has(`p:${id}`));

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 4000,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
         onClick={() => !busy && onClose()}>
      <div style={{ background: "#fff", borderRadius: 12, width: 720, maxWidth: "96vw", maxHeight: "92vh",
                    display: "flex", flexDirection: "column", boxShadow: "0 8px 40px rgba(0,0,0,0.25)" }}
           onClick={e => e.stopPropagation()}>

        <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid #eef0f6" }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#1a3a6e" }}>Draft from notes</div>
          <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
            {draft
              ? "Nothing is applied yet. Uncheck anything that's wrong, then apply what's left."
              : "Paste what you have. You'll review everything before it touches the agreement."}
          </div>
        </div>

        <div style={{ overflow: "auto", padding: "16px 22px" }}>
          {!draft ? (
            <textarea className="input" value={notes} onChange={e => setNotes(e.target.value)}
                      placeholder={PLACEHOLDER} disabled={busy}
                      style={{ width: "100%", boxSizing: "border-box", minHeight: 260, fontSize: 13,
                               lineHeight: 1.55, fontFamily: "inherit", resize: "vertical" }} />
          ) : (
            <>
              {draft.summary && (
                <div style={{ background: "#f6f8ff", border: "1px solid #e0e6ff", borderRadius: 8,
                              padding: "11px 13px", fontSize: 13, lineHeight: 1.55, color: "#2a3550", marginBottom: 16 }}>
                  {draft.summary}
                </div>
              )}

              {draft.products.length > 0 ? (
                <Group title="Modules">
                  {draft.products.map(id => (
                    <Row key={id} checked={isOn(`p:${id}`)} onToggle={() => toggle(`p:${id}`)} title={nameOf(id)} />
                  ))}
                </Group>
              ) : (
                <div style={{ fontSize: 13, color: "#a3342a", marginBottom: 14 }}>
                  Nothing in the notes matched a module in the library. Build this one by hand.
                </div>
              )}

              {(draft.hosting.length > 0 || draft.uds.length > 0) && (
                <Group title="Options">
                  {draft.hosting.map(id => (
                    <Row key={`h${id}`} checked={isOn(`h:${id}`)} onToggle={() => toggle(`h:${id}`)}
                         title="Hosted by DHIT" detail={nameOf(id)} />
                  ))}
                  {draft.uds.map(id => (
                    <Row key={`u${id}`} checked={isOn(`u:${id}`)} onToggle={() => toggle(`u:${id}`)}
                         title="Add UDS Reporting" detail={nameOf(id)} />
                  ))}
                </Group>
              )}

              {draft.excludeAddons.length > 0 && (
                <Group title="Leave out of this quote">
                  {draft.excludeAddons.map(a => (
                    <Row key={a.rowId} checked={isOn(`x:${a.rowId}`)} onToggle={() => toggle(`x:${a.rowId}`)}
                         title={a.item} detail={a.table} />
                  ))}
                </Group>
              )}

              {draft.priceOverrides.length > 0 && (
                <Group title="Price changes">
                  {draft.priceOverrides.map(o => (
                    <Row key={o.rowId} checked={isOn(`$:${o.rowId}`)} onToggle={() => toggle(`$:${o.rowId}`)}
                         title={o.item} detail={`${o.from || "—"} → ${o.price}`} note={o.reason} />
                  ))}
                </Group>
              )}

              {(draft.fields.length > 0 || draft.agreementTitle) && (
                <Group title="Details">
                  {draft.fields.map(f => (
                    <Row key={f.key} checked={isOn(`f:${f.key}`)} onToggle={() => toggle(`f:${f.key}`)}
                         title={f.key} detail={f.value} />
                  ))}
                  {draft.agreementTitle && (
                    <Row checked={isOn("title")} onToggle={() => toggle("title")}
                         title="Agreement title" detail={draft.agreementTitle} />
                  )}
                </Group>
              )}

              {draft.questions.length > 0 && (
                <div style={{ background: "#fff8e1", border: "1px solid #fcd34d", borderRadius: 8,
                              padding: "11px 13px", marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#795548", letterSpacing: ".04em", marginBottom: 5 }}>
                    WORTH CHECKING
                  </div>
                  {draft.questions.map((q, i) => (
                    <div key={i} style={{ fontSize: 12.5, color: "#5d4037", lineHeight: 1.5 }}>• {q}</div>
                  ))}
                </div>
              )}

              {draft.dropped.length > 0 && (
                <details style={{ fontSize: 12, color: "#8a90a6" }}>
                  <summary style={{ cursor: "pointer" }}>
                    {draft.dropped.length} suggestion{draft.dropped.length !== 1 ? "s" : ""} didn't match the catalog
                  </summary>
                  <div style={{ marginTop: 6, paddingLeft: 4 }}>
                    {draft.dropped.map((d, i) => <div key={i} style={{ lineHeight: 1.5 }}>• {d}</div>)}
                  </div>
                </details>
              )}
            </>
          )}

          {error && (
            <div style={{ marginTop: 12, padding: "10px 12px", background: "#fdecea", border: "1px solid #f5c6cb",
                          borderRadius: 6, fontSize: 12, color: "#a3342a" }}>
              {error}
            </div>
          )}
        </div>

        <div style={{ padding: "14px 22px", borderTop: "1px solid #eef0f6", display: "flex",
                      justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 11, color: "#aab" }}>
            {draft ? "Applying replaces the current selection." : "Reviewed before anything is applied."}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            {draft && (
              <button onClick={() => { setDraft(null); setError(null); }}
                      style={{ padding: "9px 16px", borderRadius: 7, border: "1px solid #ddd", background: "#fff",
                               color: "#555", fontSize: 13, cursor: "pointer" }}>
                Back to notes
              </button>
            )}
            <button onClick={onClose} disabled={busy}
                    style={{ padding: "9px 18px", borderRadius: 7, border: "1px solid #ddd", background: "#fff",
                             color: "#555", fontSize: 13, cursor: "pointer" }}>
              Cancel
            </button>
            <button onClick={draft ? apply : build}
                    disabled={busy || (!draft && !notes.trim()) || (draft && nothingLeft)}
                    style={{ padding: "9px 20px", borderRadius: 7, border: "none", background: "#1a3a6e", color: "#fff",
                             fontSize: 13, fontWeight: 600,
                             cursor: busy ? "wait" : "pointer",
                             opacity: busy || (!draft && !notes.trim()) || (draft && nothingLeft) ? 0.5 : 1 }}>
              {busy ? "Reading the notes…" : draft ? "Apply to agreement" : "Build draft"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
